import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export default function LoginPage() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (role) => {
    if (role === "admin") { setEmail("accounts@theglobalchecks.com"); setPassword("GlobalChecks@2026"); }
    else { setEmail("demo.client@theglobalchecks.com"); setPassword("Client@Demo2026"); }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <div className="flex justify-center mb-8"><Logo /></div>
      <div className="border border-slate-200 rounded-2xl bg-white p-8">
        <h1 className="font-display text-2xl font-bold text-slate-900">Sign in</h1>
        <p className="text-sm text-slate-500 mt-1">Access your client or admin portal.</p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="mt-1.5" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs text-[#1E3A8A] hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
            </div>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="mt-1.5" />
          </div>
          <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full pill-btn bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <div className="mt-6 grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => fillDemo("client")} data-testid="login-demo-client" className="rounded-full text-xs">Use demo client</Button>
          <Button variant="outline" onClick={() => fillDemo("admin")} data-testid="login-demo-admin" className="rounded-full text-xs">Use admin</Button>
        </div>
        <p className="mt-6 text-sm text-slate-600 text-center">
          No account? <Link to="/register" className="text-[#1E3A8A] font-semibold hover:underline">Sign up as a client</Link>
        </p>
      </div>
    </div>
  );
}
